import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styles from './NavLayout.module.scss';
import { IconInfo } from '@src/constants';
import { LordIcon } from '@src/components/lord-icon/lord-icon';
import IconBack from 'src/assets/icons/icon-back.svg?react';
import ChevronDown from 'src/assets/icons/dropdown.svg?react';
import IconOutline from "src/assets/icons/new-outline-info.svg?react";
import { useHistory } from 'react-router-dom';
import { Dropdown, Tooltip } from "antd";
import type { MenuProps } from 'antd';
import { SllProgram } from '@src/api/graphql-codegen';
import { generateSlug } from '@src/utils/helpers';
import ProgramPurpleBanner from '@src/components/program-description/ProgramPurpleBanner';

type Props = {
  program: SllProgram;
  allPrograms: SllProgram[];
  handleSelectProgram?: (program: SllProgram) => void;
  movedScroll?: boolean;
  isAUuser?: boolean;
}

function ProgramNavLayout(props: Props) {
  const { program, allPrograms=[], movedScroll, isAUuser, handleSelectProgram } = props;
  const history = useHistory();

  const [showProgramMenu, setShowProgramMenu] = useState(false);

  const handleBack = () => {
    history.replace("/lessons?tab=programs");
  }

  let programOptions = allPrograms.map(program => {
    return {
      label: program.name,
      value: generateSlug(program.name),
      hoverText: program.hoverText,
      iconUrl: program.iconUrl
    }
  })
  programOptions.push({
    label: isAUuser ? "Standalone Lessons" : "Lesson Library",
    value: "/lessons?tab=library",
    hoverText: 'This is the hub for all lessons available on Open Parachute. Lessons are organized by grade and further categorized by theme. Search for lessons based on grade, topic, lesson type, tier and competency.',
    iconUrl: "https://cdn.lordicon.com/pmfpsjek.json"
  })

  useEffect(() => {
    if(movedScroll) {
      setShowProgramMenu(false)
    }
  }, [movedScroll])

  const IconSrc = program?.iconUrl || 'https://cdn.lordicon.com/pmfpsjek.json';

  const selectProgram = useCallback((programName: string) => {
    const selProgram = allPrograms.find(program => program.name === programName)
    if (selProgram) {
      handleSelectProgram?.(selProgram);
      setShowProgramMenu(false);
    } else if (programName === 'Lesson Library' || programName === 'Standalone Lessons') {
      history.replace('/lessons?tab=library')
      setShowProgramMenu(false);
    }
  }, [allPrograms, history, handleSelectProgram]);

  const programMenu = useMemo(( )=> {
    const items: MenuProps['items'] = (programOptions || []).filter(prog => prog.label !== program?.name)
      .map((prog, index) => {
      const IconSrc = prog.iconUrl || 'https://cdn.lordicon.com/pmfpsjek.json';

      const text = prog.hoverText;
      const boldText = text?.replace(prog.label, `<strong>${prog.label}</strong>`);

      return {
        key: index,
        label: (
        <div className={`${styles.gradeDropDownMenuitem}`}>
            <div style={{display: 'flex', gap: '16px'}}>
              <LordIcon src={IconSrc} isHover={true} colors={{primary: '#274649', secondary: '#274649'}} target='.NavLayout_gradeDropDownMenuitem__sBqfP' size={50}/>
              <span>{prog.label}</span>
            </div>
            <Tooltip
              placement="rightTop"
              title={
                <div className={styles.cardBack}>
                  <IconOutline className={styles.infoicon} />
                  <div className={styles.hoverCardText}>
                    <div className={styles.span1} dangerouslySetInnerHTML={{ __html: boldText ?? "" }} />
                  </div>
                </div>
              }
              overlayStyle={{ maxWidth: "400px" }}
              showArrow={false}
              color="#FFFFFF"
            >
              <div className={styles.menuRightIcon}>
                <LordIcon src={IconInfo} isHover={true} colors={{ primary: "#6C969A", secondary: "#6C969A" }} size={26} />
              </div>
            </Tooltip>
        </div>
        ),
        onClick:() => {selectProgram(prog.label)}
      }
    })
    return items
  }, [program, programOptions, selectProgram])

  return (
    <>
      <div id={"navMenuBar"} className={styles.navView}>
        <div className={styles.itemWrapper}>
          <Dropdown menu={{ items: programMenu }} trigger={['click']} placement="bottom" onOpenChange={(visible) => setShowProgramMenu(visible)} open={showProgramMenu}>
            <div className={[styles.listItem, styles.selItem].join(' ')}>
              <LordIcon src={IconSrc} isHover={true} colors={{primary: '#FFB073', secondary: '#FFB073'}} target='.NavLayout_gradeDropDownMenuitem__sBqfP' size={60}/>
              <div className={[styles.listTitle, styles.selItemTitle].join(' ')}>
              {program?.name}
              </div>
              {<div className={[styles.dropdownToggle, showProgramMenu ? styles.dropdownToggleClose : styles.dropdownToggleOpen].join(' ')}>
                <ChevronDown className={styles.selicon}/>
              </div>}
            </div>
          </Dropdown>
        </div>
        {<div className={styles.iconBack} onClick={handleBack}>
          <IconBack />
        </div>}
      </div>
      {program.bannerDesc && <ProgramPurpleBanner program={program} />}
    </>
  );
}

export default ProgramNavLayout;
